import { BrandIdKeys } from '@seek/brand-types';
import { extractQuestionnaireId } from '@seek/rr-caja';

import { cleanObject } from 'src/app/utils/cleanObject';
import { mapTextToRestrictedEmojisText } from 'src/app/utils/emoji';
import {
  mapSalaryTypeGraphToPayTypeUI,
  getSalaryCurrency,
  majorToMinor,
  minorToMajor,
} from 'src/app/utils/salary/getSalary';
import {
  type CreateDraftJobInput,
  type DraftJobSalaryCurrency,
  type DraftJobSuccessPayload,
  DraftJobWritingMode,
  GuidedDetailsType,
} from 'src/graphql/generated';
import type { FormData, RootState } from 'src/types/appState';
import type { DraftPayload } from 'src/types/draft';
import { ApplyViaOptions } from 'src/types/linkOut';
import type { PayType } from 'src/types/salaryTypes';
import {
  workTypeGraphQLToUI,
  flowTypeUIToGraphQL,
  payTypeUIToGraphQL,
  workTypeUIToGraphQL,
  draftProgressUIToGraphQL,
} from 'src/types/serializer';
import type { CountryCode } from 'src/types/taxonomy';

import {
  getGuidedDetail,
  getQuestionnaireUrlById,
  getCategoryIds,
  type MapProductsOverrides,
  mapGuidedDetails,
  mapKeySellingPoints,
  mapProducts,
} from './utils';
import { sanitiseInput } from './utils/sanitiseInput';
import verifyBoolean from './utils/verifyBoolean';
import verifyString from './utils/verifyString';

interface Features {
  isBrandingEnabled: boolean;
  isKeySellingPointsEnabled: boolean;
  shouldRestrictEmojis: boolean;
}

interface Props {
  countryCode: CountryCode;
  hasLinkOutEnabled: boolean;
  features: Features;
}

export const mapGetDraftResponseToDraftPayload =
  (payload: DraftJobSuccessPayload) =>
  ({ countryCode, hasLinkOutEnabled, features }: Props): DraftPayload => {
    const { draft } = payload;

    const salaryCurrency = getSalaryCurrency({
      formDataCurrency: draft.salary?.currency ?? null,
      hirerNation: countryCode,
      jobLocationCountry: null,
    }) as DraftJobSalaryCurrency;

    const { categoryId, subCategoryId } = getCategoryIds(draft);

    const maxSalaryValueInDollars = minorToMajor(
      salaryCurrency,
      draft.salary?.maximum,
    );
    const minSalaryValueInDollars = minorToMajor(
      salaryCurrency,
      draft.salary?.minimum,
    );

    const selectedProductId = draft.products?.productIds
      ? draft.products.productIds[0]
      : undefined;

    const draftPayload: DraftPayload = {
      aboutBusiness: getGuidedDetail({
        guidedDetails: draft.guidedDetails,
        type: GuidedDetailsType.ABOUT_BUSINESS,
      }),
      applicationFormUrl:
        hasLinkOutEnabled && draft.applicationFormUrl
          ? draft.applicationFormUrl
          : '',
      benefits: getGuidedDetail({
        guidedDetails: draft.guidedDetails,
        type: GuidedDetailsType.BENEFITS,
      }),
      brandId: draft.branding?.id || '',
      categoryId: Number(categoryId) || undefined,
      displayPay: !Boolean(draft.salary?.isHidden),
      expiryDate: draft.expiresAt || '',
      hasAIAssistanceBeenUsed: draft.hasAIAssistanceBeenUsed === true,
      internalReference: draft.jobAdReference || '',
      jobDescription: draft.details || '',
      jobLocationId: draft?.locationsV2?.[0]?.id,
      jobSummary:
        mapTextToRestrictedEmojisText(
          draft.summary || '',
          features.shouldRestrictEmojis,
        ) || '',
      jobTitle: mapTextToRestrictedEmojisText(
        draft.titles?.[0]?.title || '',
        features.shouldRestrictEmojis,
      ),
      isGuided: false,
      keySellingPoint1: mapTextToRestrictedEmojisText(
        draft.keySellingPoints?.[0] || '',
        features.shouldRestrictEmojis,
      ),
      keySellingPoint2: mapTextToRestrictedEmojisText(
        draft.keySellingPoints?.[1] || '',
        features.shouldRestrictEmojis,
      ),
      keySellingPoint3: mapTextToRestrictedEmojisText(
        draft.keySellingPoints?.[2] || '',
        features.shouldRestrictEmojis,
      ),
      maxSalary: maxSalaryValueInDollars,
      minSalary: minSalaryValueInDollars,
      payType: mapSalaryTypeGraphToPayTypeUI(draft.salary?.type),
      qualifications: getGuidedDetail({
        guidedDetails: draft.guidedDetails,
        type: GuidedDetailsType.QUALIFICATIONS,
      }),
      questionnaireUrl: draft.questionnaireId
        ? getQuestionnaireUrlById(draft.questionnaireId)
        : '',
      responsibilities: getGuidedDetail({
        guidedDetails: draft.guidedDetails,
        type: GuidedDetailsType.RESPONSIBILITIES,
      }),
      salaryCurrency,
      salaryText: mapTextToRestrictedEmojisText(
        draft.salary?.displayValue || '',
        features.shouldRestrictEmojis,
      ),
      selectedProductId: selectedProductId || undefined,
      subCategoryId: Number(subCategoryId) || undefined,
      videoUrl: draft.video?.url,
      workArrangements: draft.workArrangements ?? undefined,
      workType: draft?.workTypes?.[0]
        ? workTypeGraphQLToUI[draft?.workTypes?.[0]]
        : '',
    };

    if (draft.isConfidential !== null && draft.isConfidential !== undefined) {
      draftPayload.isConfidential = draft.isConfidential;
    }

    if (draft.phoneNumber !== null && draft.phoneNumber !== undefined) {
      draftPayload.phoneNumber = draft.phoneNumber;
    }

    return draftPayload;
  };

export type MapToDraftJobInputOverrides = {
  jobLocationId?: string;
  productSet?: MapProductsOverrides['productSet'];
  selectedProductId?: MapProductsOverrides['selectedProductId'];
};

export const mapToDraftJobInput = (
  formData: FormData,
  session: RootState['session'],
  features: Features,
  overrides?: MapToDraftJobInputOverrides,
): CreateDraftJobInput => {
  const currency = verifyString(
    getSalaryCurrency({
      formDataCurrency: formData.salaryCurrency,
      hirerNation: session?.hirerAccount?.nation,
      jobLocationCountry: session?.jobLocationCountry,
    }),
  );

  const maxSalaryValueInCents = majorToMinor(currency, formData.maxSalary);
  const minSalaryValueInCents = majorToMinor(currency, formData.minSalary);

  const canMapBranding = features.isBrandingEnabled || session.buyUpSell;
  const canMapKeySellingPoints =
    features.isKeySellingPointsEnabled || session.buyUpSell;

  const locationId =
    verifyString(overrides?.jobLocationId) ??
    verifyString(formData.jobLocationId);

  return cleanObject({
    adcentreAdvertiserId: session.advertiserId,
    applicationFormUrl:
      formData.linkOutApplyVia === ApplyViaOptions.SEEK_WEBSITE
        ? null
        : verifyString(formData.applicationFormUrl),
    branding: canMapBranding
      ? {
          id: formData.brand?.[BrandIdKeys.id],
          legacyId: formData.brand?.legacyId,
        }
      : null,
    categories: [
      {
        adcentreCategoryId: verifyString(formData.categoryId),
        adcentreSubCategoryId: verifyString(formData.subCategoryId),
        categoryId: verifyString(session.seekClassificationId),
        subCategoryId: verifyString(session.seekSubClassificationId),
      },
    ],
    details: verifyString(sanitiseInput(formData.jobDescription)),
    draftProgress: draftProgressUIToGraphQL[formData.draftProgress],
    expiresAt: formData.expiryDate ? verifyString(formData.expiryDate) : null,
    ...(formData.flowType !== 'refresh' && {
      flowType: flowTypeUIToGraphQL[formData.flowType],
    }),
    guidedDetails: mapGuidedDetails(formData),
    isConfidential: formData.isConfidential,
    jobAdReference: verifyString(formData.jobAdReference),
    jobWritingMode: DraftJobWritingMode.STANDARD,
    keySellingPoints: canMapKeySellingPoints
      ? mapKeySellingPoints(formData)
      : null,
    ...(locationId && {
      locations: [{ id: locationId }],
    }),
    phoneNumber: formData.phoneNumber,
    products: mapProducts(formData, session, overrides),
    questionnaireId: verifyString(
      extractQuestionnaireId(formData.questionnaireUrl),
    ),
    salary: {
      currency,
      displayValue: verifyString(formData.salaryText),
      isHidden: verifyBoolean(!formData.displayPay),
      maximum: maxSalaryValueInCents,
      minimum: minSalaryValueInCents,
      type: payTypeUIToGraphQL[formData.payType as PayType], //  payType in formData is defined as string
    },
    summary: verifyString(formData.jobSummary),
    ...(verifyString(formData.jobTitle) && {
      titles: [
        {
          title: formData.jobTitle,
          language: 'en-AU',
        },
      ],
    }),
    video: {
      url: verifyString(formData.videoUrl),
    },
    workTypes: [workTypeUIToGraphQL[formData.workType]],
    workArrangements: formData.workArrangement
      ? [formData.workArrangement]
      : undefined,
  });
};
